import { useState } from 'react';
import { createTodoItem, updateTodoItem, deleteTodoItem, reorderTodoItems } from '../../moduleServices/planner_todos_api';
import { toast } from 'react-hot-toast';

// ##### Custom hook for todo items management
export const useTodoItems = (todo, setTodo) => {
  const [loadingTodo, setLoadingTodo] = useState({});

  // ##### Add new item to todo
  const addItem = async (todoId, text) => {
    const todoItem = todo.find(t => t.id === todoId);
    const currentItems = todoItem.items || [];
    
    // Calculate order for new item (add at the end)
    const maxOrder = currentItems.length > 0 ? Math.max(...currentItems.map(item => item.order ?? 0)) : -1;
    const order = maxOrder + 1;
    
    const tempItem = { id: `temp-${Date.now()}`, text, done: false, order };
    
    // Set loading for todo
    setLoadingTodo(prev => ({ ...prev, [todoId]: true }));
    
    // Optimistic update - add at the end
    setTodo(prev => prev.map(t => 
      t.id === todoId 
        ? { ...t, items: [...(t.items || []), tempItem] }
        : t
    ));

    try {
      const response = await createTodoItem({ text, todo: todoId, order });
      
      // Update with real item from server
      setTodo(prev => prev.map(t => 
        t.id === todoId 
          ? { ...t, items: (t.items || []).map(item => item.id === tempItem.id ? response : item) }
          : t
      ));
      
    } catch (error) {
      console.error('Error adding item:', error);
      
      // Revert optimistic update
      setTodo(prev => prev.map(t => 
        t.id === todoId 
          ? { ...t, items: (t.items || []).filter(item => item.id !== tempItem.id) }
          : t
      ));
      
      toast.error('Failed to add item. Please try again.', { closeButton: true });
    } finally {
      setLoadingTodo(prev => ({ ...prev, [todoId]: false }));
    }
  };

  // ##### Edit item
  const editItem = async (todoId, itemId, newText) => {
    const todoItem = todo.find(t => t.id === todoId);
    const originalItem = (todoItem.items || []).find(i => i.id === itemId);
    if (!originalItem) return;

    // Set loading for todo
    setLoadingTodo(prev => ({ ...prev, [todoId]: true }));

    // Optimistic update
    setTodo(prev => prev.map(t =>
      t.id === todoId
        ? { ...t, items: (t.items || []).map(i => i.id === itemId ? { ...i, text: newText } : i) }
        : t
    ));

    try {
      const updated = await updateTodoItem(itemId, { text: newText });
      setTodo(prev => prev.map(t =>
        t.id === todoId
          ? { ...t, items: (t.items || []).map(i => i.id === itemId ? updated : i) }
          : t
      ));
    } catch (error) {
      // Revert on error
      setTodo(prev => prev.map(t =>
        t.id === todoId
          ? { ...t, items: (t.items || []).map(i => i.id === itemId ? originalItem : i) }
          : t
      ));
      toast.error('Failed to edit item');
    } finally {
      // Clear loading
      setLoadingTodo(prev => {
        const newState = { ...prev };
        delete newState[todoId];
        return newState;
      });
    }
  };

  // ##### Delete item
  const deleteItem = async (todoId, itemId) => {
    const todoItem = todo.find(t => t.id === todoId);
    const item = (todoItem.items || []).find(i => i.id === itemId);
    if (!item) return;

    // Set loading for todo
    setLoadingTodo(prev => ({ ...prev, [todoId]: true }));

    // Optimistic update
    setTodo(prev => prev.map(t =>
      t.id === todoId ? { ...t, items: (t.items || []).filter(i => i.id !== itemId) } : t
    ));

    try {
      await deleteTodoItem(itemId);
    } catch (error) {
      // Revert on error
      setTodo(prev => prev.map(t =>
        t.id === todoId ? { ...t, items: [...(t.items || []), item] } : t
      ));
      toast.error('Failed to delete item');
    } finally {
      // Clear loading
      setLoadingTodo(prev => {
        const newState = { ...prev };
        delete newState[todoId];
        return newState;
      });
    }
  };

  // ##### Toggle item done status
  const toggleItem = async (todoId, itemId) => {
    const todoItem = todo.find(t => t.id === todoId);
    const item = (todoItem.items || []).find(i => i.id === itemId);
    if (!item) return;

    // Set loading for todo
    setLoadingTodo(prev => ({ ...prev, [todoId]: true }));

    // Optimistic update
    setTodo(prev => prev.map(t =>
      t.id === todoId
        ? { ...t, items: (t.items || []).map(i => i.id === itemId ? { ...i, done: !i.done } : i) }
        : t
    ));

    try {
      const updated = await updateTodoItem(itemId, { done: !item.done });
      setTodo(prev => prev.map(t =>
        t.id === todoId
          ? { ...t, items: (t.items || []).map(i => i.id === itemId ? updated : i) }
          : t
      ));
    } catch (error) {
      // Revert on error
      setTodo(prev => prev.map(t =>
        t.id === todoId
          ? { ...t, items: (t.items || []).map(i => i.id === itemId ? item : i) }
          : t
      ));
      toast.error('Failed to update item');
    } finally {
      // Clear loading
      setLoadingTodo(prev => {
        const newState = { ...prev };
        delete newState[todoId];
        return newState;
      });
    }
  };

  // ##### Reorder items
  const reorderItems = async (todoId, newItems) => {
    const originalItems = todo.find(t => t.id === todoId)?.items || [];

    // Validate input
    if (!newItems || newItems.length === 0) {
      return;
    }

    // Set loading for todo
    setLoadingTodo(prev => ({ ...prev, [todoId]: true }));

    // Optimistic update
    setTodo(prev => prev.map(t =>
      t.id === todoId ? { ...t, items: newItems.map((item, idx) => ({ ...item, order: idx })) } : t
    ));

    try {
      // Use batch endpoint instead of multiple individual requests
      await reorderTodoItems(todoId, newItems);
    } catch (error) {
      console.error('Error reordering items:', error);
      // Revert on error
      setTodo(prev => prev.map(t =>
        t.id === todoId ? { ...t, items: originalItems } : t
      ));
      toast.error('Failed to reorder items');
    } finally {
      // Clear loading
      setLoadingTodo(prev => {
        const newState = { ...prev };
        delete newState[todoId];
        return newState;
      });
    }
  };

  return {
    loadingTodo,
    addItem,
    editItem,
    deleteItem,
    toggleItem,
    reorderItems
  };
};
